import type { ContractManagementViewInDb } from '@/api';

/**
 * Static column types available in contract management views
 */
export type StaticColumnType =
  | 'contractual_party'
  | 'matter'
  | 'owner'
  | 'effective_from'
  | 'effective_to'
  | 'created_at'
  | 'type'
  | 'tags'
  | 'state'
  | 'name';

/**
 * Entity-based static column types (have UID→name mapping)
 * These columns contain arrays of entity objects with uid and name properties
 */
export type EntityBasedStaticColumnType =
  'contractual_party' | 'matter' | 'owner' | 'effective_from' | 'effective_to';

/**
 * Primitive static column types (don't have UID→name mapping)
 * These columns contain primitive values (strings, arrays of strings, etc.)
 */
export type PrimitiveStaticColumnType = 'created_at' | 'type' | 'tags' | 'state';

/**
 * Check if a static column type is entity-based (has UID→name mapping)
 * Entity-based columns: contractual_party, matter, owner, effective_from, effective_to
 * Primitive columns: created_at, type, tags, state
 */
export function isEntityBasedStaticColumn(
  type: StaticColumnType
): type is EntityBasedStaticColumnType {
  return ['contractual_party', 'matter', 'owner', 'effective_from', 'effective_to'].includes(type);
}

/**
 * Static columns the backend can actually order by.
 *
 * The view repo resolves a sort key to an attribute of the same name on the
 * `ContractManagementContract` model and silently falls back to the contract id
 * when there is none. Only these four are columns on that model; the entity
 * columns (`owner`, `contractual_party`, `matter`, `effective_from`, `effective_to`)
 * reach a row through a join, so asking to sort by one returns id order — which
 * is why they get no sort affordance at all rather than one that does nothing.
 */
export const SORTABLE_STATIC_COLUMN_TYPES: readonly StaticColumnType[] = [
  'name',
  'type',
  'tags',
  'created_at',
];

export function isSortableStaticColumn(type: StaticColumnType): boolean {
  return SORTABLE_STATIC_COLUMN_TYPES.includes(type) || type === 'state';
}

/**
 * The `sort_key` a static column orders by: its own type, except the state
 * column, which holds two states and sorts by the contract's (worst first
 * ascending, contracts without one last).
 */
export function staticColumnSortKey(type: StaticColumnType): string | undefined {
  if (type === 'state') return 'contract_state';
  return isSortableStaticColumn(type) ? type : undefined;
}

/**
 * Information about a static column in a view
 */
export interface StaticColumnInfo {
  type: StaticColumnType;
  name: string;
  visible: boolean;
  order_index: number;
  fieldKey: string; // Unique identifier for the column (e.g., '__static_matter')
}

/**
 * Static column types that have a matching STATIC_COLUMN_CONFIGS entry.
 * The 'name' column is intentionally excluded: it has no name/visible/order_index
 * backend fields, so it cannot be renamed, hidden, or reordered like the others.
 */
export type ConfigurableStaticColumnType = Exclude<StaticColumnType, 'name'>;

/**
 * Configuration for static columns
 * Maps static column types to their view property keys
 * This allows us to dynamically extract static columns from the view data
 */
export const STATIC_COLUMN_CONFIGS: Record<
  ConfigurableStaticColumnType,
  {
    nameKey: keyof ContractManagementViewInDb;
    visibleKey: keyof ContractManagementViewInDb;
    orderIndexKey: keyof ContractManagementViewInDb;
    fieldKey: string;
  }
> = {
  contractual_party: {
    nameKey: 'contractual_party_column_name',
    visibleKey: 'contractual_party_column_visible',
    orderIndexKey: 'contractual_party_column_order_index',
    fieldKey: '__static_contractual_party',
  },
  matter: {
    nameKey: 'matter_column_name',
    visibleKey: 'matter_column_visible',
    orderIndexKey: 'matter_column_order_index',
    fieldKey: '__static_matter',
  },
  owner: {
    nameKey: 'owner_column_name',
    visibleKey: 'owner_column_visible',
    orderIndexKey: 'owner_column_order_index',
    fieldKey: '__static_owner',
  },
  effective_from: {
    nameKey: 'effective_from_column_name',
    visibleKey: 'effective_from_column_visible',
    orderIndexKey: 'effective_from_column_order_index',
    fieldKey: '__static_effective_from',
  },
  effective_to: {
    nameKey: 'effective_to_column_name',
    visibleKey: 'effective_to_column_visible',
    orderIndexKey: 'effective_to_column_order_index',
    fieldKey: '__static_effective_to',
  },
  created_at: {
    nameKey: 'created_at_column_name',
    visibleKey: 'created_at_column_visible',
    orderIndexKey: 'created_at_column_order_index',
    fieldKey: '__created_at',
  },
  type: {
    nameKey: 'document_type_column_name',
    visibleKey: 'document_type_column_visible',
    orderIndexKey: 'document_type_column_order_index',
    fieldKey: '__type',
  },
  tags: {
    nameKey: 'contract_tags_column_name',
    visibleKey: 'contract_tags_column_visible',
    orderIndexKey: 'contract_tags_column_order_index',
    fieldKey: '__tags',
  },
  state: {
    nameKey: 'state_column_name',
    visibleKey: 'state_column_visible',
    orderIndexKey: 'state_column_order_index',
    fieldKey: '__static_state',
  },
};

/**
 * One column in the order the Columns popover hands back after a drag.
 * Static and dynamic columns are addressed differently because they persist
 * through different endpoints: a static column's position lives on the view
 * itself (`<prefix>_column_order_index`), a view column's on the column.
 */
export type ViewColumnOrderItem =
  { kind: 'static'; type: ConfigurableStaticColumnType } | { kind: 'dynamic'; uid: string };
